import { Component, Input, OnInit } from '@angular/core';
import { LocalStorage } from 'angular-web-storage';
import { Subject } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, map } from 'rxjs/operators';

import { Association } from '../../../../../../../models/association';
import { CallbackResult, CallbackResultStatus, dateStringFromISO8601, timeStringFromISO8601 } from '../../../../../../../utils';
import { ListResponse } from '../../../../../../../models/list-response';
import { AssociationService } from '../../../../../../../services/association/association.service';
import { AccountService } from '../../../../../../../services/account/account.service';
import { ActivityLogEntry } from '../../../../../../../models/activity-log';
import { EditActivityLogEntryNotesModalComponent } from '../../../../customers/customer-detail/sub-lists/activity-log-sub-list/modals/edit-activity-log-entry-notes-modal/edit-activity-log-entry-notes-modal.component';


@Component({
  selector: 'app-association-activity-log-sub-list',
  templateUrl: './association-activity-log-sub-list.component.html',
  styleUrls: ['./association-activity-log-sub-list.component.css']
})
export class AssociationActivityLogSubListComponent implements OnInit {
  @Input() association: Association;
  @Input() resultCallback: (result: CallbackResult) => void;

  public pageNumber = 1;
  // Giving this a project-unique name ensures it is not shared with other saved
  // pageSize variables.
  @LocalStorage() public pageSizeAssociationActivityLogSubList;

  public filtersChangedSubject = new Subject<null>();

  public activityLogList: ListResponse<ActivityLogEntry>;

  public loadingActivityLog = true;

  public serverError: number = null;

  public dateStringFromISO8601 = dateStringFromISO8601;
  public timeStringFromISO8601 = timeStringFromISO8601;

  // The number of list requests. Used to ensure that only the most recent
  // request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private listRequestCount = 0;

  constructor(
    private accountService: AccountService,
    private associationService: AssociationService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    if (!this.pageSizeAssociationActivityLogSubList) {
      this.pageSizeAssociationActivityLogSubList = 10;
    }
    // This is needed in order for the pageSize to be updated when the html
    // select is first changed.
    // noinspection SillyAssignmentJS
    this.pageSizeAssociationActivityLogSubList = this.pageSizeAssociationActivityLogSubList;

    // Handle changes to the filters by reloading the list,
    // subject to debounce.
    this.filtersChangedSubject.pipe(
      map(() => { this.loadingActivityLog = true; }),
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadActivityLog();
    });

    this.loadActivityLog();
  }

  public editActivityLogEntryNotes(entry: ActivityLogEntry) {
    const modalRef = this.modalService.open(
      EditActivityLogEntryNotesModalComponent
    );
    modalRef.componentInstance.entry = entry;
    modalRef.componentInstance.savedCallback = (successMessage: string) => {
      this.resultCallback({
        text: successMessage,
        status: CallbackResultStatus.success
      });
      this.loadActivityLog();
    };
  }

  /**
   * Update the current page number to the given value and load the page of
   * results.
   *
   * @param newPageNumber - The new page number.
   */
  public setPage(newPageNumber: number) {
    this.pageNumber = newPageNumber;
    this.filtersChangedSubject.next();
  }

  /**
   * Get the number of the last page of results.
   */
  public lastPageNumber() {
    if (!this.activityLogList) { return 1; }
    return Math.ceil(this.activityLogList.count / this.pageSizeAssociationActivityLogSubList);
  }

  private loadActivityLog() {
    this.loadingActivityLog = true;

    // Increment the request count and save a local copy.
    this.listRequestCount++;
    const thisRequestCount = this.listRequestCount;

    this.associationService
      .getAssociationActivityLog(
        this.association.id,
        this.pageNumber,
        this.pageSizeAssociationActivityLogSubList
      )
      .then(response => {
        // Only update the list if there has not been a more recent request
        // initiated.
        if (thisRequestCount === this.listRequestCount) {
          this.activityLogList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.listRequestCount) {
          this.loadingActivityLog = false;
        }
      });
  }
}
